import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export interface GradientTab {
    id: string
    label: string
}

interface GradientTabsProps {
    tabs: Array<GradientTab>
    active: number
    onSelect: (index: number) => void
    panelId: string
    tone?: 'dark' | 'light'
    className?: string
    useTabIdsAsAnchors?: boolean
    idPrefix?: string
}

interface Indicator {
    left: number
    width: number
    ready: boolean
}

/** Plain text tabs above a hairline, with a gradient underline that slides to the active tab. */
export const GradientTabs: FC<GradientTabsProps> = ({
    tabs,
    active,
    onSelect,
    panelId,
    tone = 'dark',
    className,
    useTabIdsAsAnchors = false,
    idPrefix = 'tab'
}): ReactNode => {
    const listRef = useRef<HTMLDivElement>(null)
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])
    const [indicator, setIndicator] = useState<Indicator>({ left: 0, width: 0, ready: false })

    useEffect(() => {
        const measure = () => {
            const button = buttonRefs.current[active]

            if (!button) return

            setIndicator({ left: button.offsetLeft, width: button.offsetWidth, ready: true })
        }

        measure()

        const list = listRef.current
        const button = buttonRefs.current[active]

        if (list && button && list.scrollWidth > list.clientWidth) {
            const start = button.offsetLeft
            const end = start + button.offsetWidth

            if (start < list.scrollLeft || end > list.scrollLeft + list.clientWidth) {
                list.scrollTo({ left: Math.max(0, start - 16), behavior: 'smooth' })
            }
        }

        const observer = new ResizeObserver(measure)

        if (listRef.current) observer.observe(listRef.current)

        return () => observer.disconnect()
    }, [active, tabs.length])

    const tabId = (tab: GradientTab) => useTabIdsAsAnchors ? tab.id : `${idPrefix}-${tab.id}`

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        let next = -1

        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
        if (event.key === 'Home') next = 0
        if (event.key === 'End') next = tabs.length - 1

        if (next < 0) return

        event.preventDefault()
        onSelect(next)
        buttonRefs.current[next]?.focus()
    }

    return (
        <div className={cn('relative', className)}>
            <div
                ref={listRef}
                role='tablist'
                aria-orientation='horizontal'
                className='no-scrollbar relative -mb-px flex gap-6 overflow-x-auto sm:gap-9'
            >
                {tabs.map((tab, index) => {
                    const selected = index === active

                    return (
                        <button
                            key={tab.id}
                            ref={element => { buttonRefs.current[index] = element }}
                            id={tabId(tab)}
                            type='button'
                            role='tab'
                            aria-selected={selected}
                            aria-controls={panelId}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => onSelect(index)}
                            onKeyDown={event => handleKeyDown(event, index)}
                            className={cn(
                                'relative shrink-0 rounded-sm pb-4 pt-1 text-[15px] font-medium tracking-[-0.01em] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 sm:text-base',
                                tone === 'dark'
                                    ? selected ? 'text-white' : 'text-white/45 hover:text-white/75'
                                    : selected ? 'text-ink' : 'text-ink/45 hover:text-ink/75',
                                tone === 'dark' ? 'focus-visible:ring-white/60' : 'focus-visible:ring-ultramarine'
                            )}
                        >
                            {tab.label}
                        </button>
                    )
                })}

                <span
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none absolute bottom-0 left-0 h-[2px] rounded-full transition-[transform,width,opacity] duration-500 ease-calm',
                        tone === 'dark' ? 'bg-aurora' : 'bg-[linear-gradient(90deg,#ff8a66,#8f7bff_55%,#3f9bff)]',
                        indicator.ready ? 'opacity-100' : 'opacity-0'
                    )}
                    style={{ width: indicator.width, transform: `translate3d(${indicator.left}px, 0, 0)` }}
                />
            </div>

            <div aria-hidden='true' className={cn('h-px w-full', tone === 'dark' ? 'bg-white/10' : 'bg-ink/10')} />
        </div>
    )
}
